import { AccessoriesInterface } from 'ks-common';

export type SnackTrayPromoVariant = 'single' | 'double';

export interface SnackTrayPromoContent {
  title: string;
  description: string;
  note: string;
}

// Single source of truth for the snack-tray promotion: which ProductIDs are
// promoted, and whether each is a single or a double. The predicates and the
// callout copy are all derived from this map so the surfaces cannot drift
// apart (ADR-004). The BOB Revolution Duallie (ProductID 6) is deliberately
// absent despite being a Type-2 double.
const SNACK_TRAY_PROMO_VARIANTS: Record<number, SnackTrayPromoVariant> = {
  1: 'single',
  5: 'single',
  15: 'single',
  2024: 'single',
  2: 'double',
  16: 'double',
};

const SNACK_TRAY_PROMO_COPY: Record<
  SnackTrayPromoVariant,
  SnackTrayPromoContent
> = {
  single: {
    title: 'Snack Tray',
    description:
      "Just released — keep your kids' snacks within reach during park days.",
    note: 'limited availability',
  },
  double: {
    title: 'Snack Tray',
    description:
      'New for the doubles — keeps snacks within reach during park days.',
    note: 'limited availability',
  },
};

export const SNACK_TRAY_ACCESSORY_ABBREVIATION = 'CST';

export const SNACK_TRAY_AVAILABLE_LABEL = 'snack tray now available';

// Category keys that surface the promo (a "NEW" chip on the type tab and
// section heading). Kept beside the per-product variants so the category-level
// and per-product surfaces share one source of truth (ADR-004).
const SNACK_TRAY_PROMO_CATEGORY_KEYS = new Set<string>(['doubles']);

export const isSnackTrayPromoCategory = (categoryKey?: string): boolean =>
  !!categoryKey && SNACK_TRAY_PROMO_CATEGORY_KEYS.has(categoryKey);

const promoVariant = (productId?: number): SnackTrayPromoVariant | undefined =>
  typeof productId === 'number'
    ? SNACK_TRAY_PROMO_VARIANTS[productId]
    : undefined;

export const isSnackTrayPromoDouble = (productId?: number): boolean =>
  promoVariant(productId) === 'double';

export const getSnackTrayPromo = (
  productId?: number,
): SnackTrayPromoContent | undefined => {
  const variant = promoVariant(productId);
  return variant ? SNACK_TRAY_PROMO_COPY[variant] : undefined;
};

export const isSnackTrayAccessory = (
  accessory?: Pick<AccessoriesInterface, 'Abbreviation'>,
): boolean => accessory?.Abbreviation === SNACK_TRAY_ACCESSORY_ABBREVIATION;
